import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Send } from 'lucide-react'
import { Card } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { cn } from '../lib/utils'

// ─── Types ────────────────────────────────────────────────────────────────────

type ConvType = 'maintenance' | 'payment' | 'general' | 'lease'
type MessageFrom = 'me' | 'them'

type Message = { id: string; from: MessageFrom; body: string; time: string; read: boolean }
type Conversation = {
  id: string; name: string; avatar: string; unit: string; subject: string
  lastMsg: string; time: string; unread: number; type: ConvType
  messages: Message[]
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const CONVERSATIONS: Conversation[] = [
  {
    id: 'c1', name: 'Jordan Blake', avatar: 'JB', unit: '4B · Maple Grove', subject: 'HVAC Issue',
    lastMsg: "The technician came by but it's still warm.", time: '10:32 AM', unread: 2, type: 'maintenance',
    messages: [
      { id: 'm1', from: 'them', body: 'Hi, the AC isn\'t cooling at all. Been like this since yesterday.', time: '9:15 AM', read: true },
      { id: 'm2', from: 'me', body: 'Hi Jordan — I\'ve dispatched Northstar HVAC, they\'ll be there by 2 PM today.', time: '9:42 AM', read: true },
      { id: 'm3', from: 'them', body: 'The technician came by but it\'s still warm. He said the part needs to be ordered.', time: '10:32 AM', read: false },
      { id: 'm4', from: 'them', body: 'Can you give me an update on when it will be fixed? It\'s very hot in here.', time: '10:33 AM', read: false },
    ],
  },
  {
    id: 'c2', name: 'Avery Kim', avatar: 'AK', unit: '12C · Willow Creek', subject: 'July Rent',
    lastMsg: 'I\'ll pay by end of week, sorry for delay.', time: 'Yesterday', unread: 1, type: 'payment',
    messages: [
      { id: 'm1', from: 'me', body: 'Hi Avery — a friendly reminder that July rent of $3,100 was due July 1st.', time: 'Mon 9:00 AM', read: true },
      { id: 'm2', from: 'them', body: 'I know, I\'m so sorry — I had an unexpected expense. I\'ll pay by end of week.', time: 'Mon 2:15 PM', read: false },
    ],
  },
  {
    id: 'c3', name: 'Sam Porter', avatar: 'SP', unit: '8 · Cedar Ridge', subject: 'Renewal Question',
    lastMsg: 'Thanks, I\'ll review and get back to you.', time: 'Mon', unread: 0, type: 'lease',
    messages: [
      { id: 'm1', from: 'me', body: 'Hi Sam! Your lease expires Jan 31. We\'d love to renew. Proposed rent: $2,496/mo (+4%). Let me know!', time: 'Mon 10:00 AM', read: true },
      { id: 'm2', from: 'them', body: 'Thanks for the heads up! I\'ll review and get back to you this week.', time: 'Mon 3:45 PM', read: true },
    ],
  },
  {
    id: 'c4', name: 'Rapid Plumbing', avatar: 'RP', unit: 'Vendor', subject: 'Invoice #2847',
    lastMsg: 'Invoice attached for the sink repair.', time: 'Jul 7', unread: 0, type: 'maintenance',
    messages: [
      { id: 'm1', from: 'them', body: 'Hi, please find attached invoice #2847 for $420 for the sink leak repair at Willow Creek 12C.', time: 'Jul 7 11:00 AM', read: true },
      { id: 'm2', from: 'me', body: 'Thank you — will process payment within 5 business days.', time: 'Jul 7 2:00 PM', read: true },
    ],
  },
  {
    id: 'c5', name: 'Tyler Washington', avatar: 'TW', unit: 'Applicant · 2A', subject: 'Showing Confirmation',
    lastMsg: 'I\'ll be there at 2 PM. Thank you!', time: 'Jul 7', unread: 0, type: 'general',
    messages: [
      { id: 'm1', from: 'me', body: 'Hi Tyler! Confirming your showing for Unit 2A – Maple Grove on July 8 at 2:00 PM. Let me know if anything changes.', time: 'Jul 6 4:00 PM', read: true },
      { id: 'm2', from: 'them', body: 'I\'ll be there at 2 PM. Very excited to see the unit! Thank you!', time: 'Jul 7 9:00 AM', read: true },
    ],
  },
]

const TYPE_STYLE: Record<ConvType, string> = {
  maintenance: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800',
  payment: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-800',
  general: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600',
  lease: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/30 dark:text-violet-300 dark:border-violet-800',
}

const FILTERS = ['All', 'Unread', 'maintenance', 'payment', 'lease'] as const
type Filter = typeof FILTERS[number]

// ─── Conversation Item ────────────────────────────────────────────────────────

function ConvItem({ conv, active, onClick }: { conv: Conversation; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={cn(
      'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors border-b border-slate-100 dark:border-slate-800',
      active ? 'bg-violet-50 dark:bg-violet-950/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800/40'
    )}>
      <div className={cn(
        'h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5',
        active ? 'bg-violet-600 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200'
      )}>{conv.avatar}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className={cn('text-sm truncate', conv.unread > 0 ? 'font-bold text-slate-900 dark:text-slate-100' : 'font-medium text-slate-700 dark:text-slate-300')}>{conv.name}</p>
          <span className="text-[11px] text-slate-400 shrink-0">{conv.time}</span>
        </div>
        <p className="text-xs text-slate-500 truncate mt-0.5">{conv.subject}</p>
        <p className={cn('text-xs truncate mt-0.5', conv.unread > 0 ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400 dark:text-slate-500')}>{conv.lastMsg}</p>
        <div className="flex items-center gap-2 mt-1.5">
          <span className={cn('text-[10px] rounded-full border px-2 py-0.5 font-semibold', TYPE_STYLE[conv.type])}>{conv.type}</span>
          {conv.unread > 0 && (
            <span className="h-4 w-4 rounded-full bg-violet-600 text-white text-[10px] font-bold flex items-center justify-center">{conv.unread}</span>
          )}
        </div>
      </div>
    </button>
  )
}

// ─── Message Thread ───────────────────────────────────────────────────────────

function MessageThread({ conv, onSend }: { conv: Conversation; onSend: (id: string, msg: string) => void }) {
  const [draft, setDraft] = useState('')

  const handleSend = () => {
    if (!draft.trim()) return
    onSend(conv.id, draft.trim())
    setDraft('')
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 px-4 py-3">
        <div className="h-8 w-8 rounded-full bg-violet-600 flex items-center justify-center text-xs font-bold text-white">{conv.avatar}</div>
        <div>
          <p className="text-sm font-bold text-slate-900 dark:text-slate-100">{conv.name}</p>
          <p className="text-xs text-slate-500">{conv.unit} · {conv.subject}</p>
        </div>
        <span className={cn('ml-auto rounded-full border px-2.5 py-0.5 text-[11px] font-semibold', TYPE_STYLE[conv.type])}>{conv.type}</span>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {conv.messages.map(msg => (
          <motion.div key={msg.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
            className={cn('flex', msg.from === 'me' ? 'justify-end' : 'justify-start')}>
            <div className={cn(
              'max-w-[78%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
              msg.from === 'me'
                ? 'bg-violet-600 text-white rounded-br-sm'
                : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded-bl-sm shadow-sm'
            )}>
              {msg.body}
              <p className={cn('text-[10px] mt-1', msg.from === 'me' ? 'text-violet-200' : 'text-slate-400')}>{msg.time}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="border-t border-slate-200 dark:border-slate-700 p-3">
        <div className="flex gap-2">
          <Input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }}
            placeholder={`Reply to ${conv.name}...`}
            className="flex-1 rounded-xl border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm"
          />
          <Button onClick={handleSend} disabled={!draft.trim()} className="bg-violet-600 hover:bg-violet-700 text-white px-4 rounded-xl">
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-[10px] text-slate-400 mt-1.5 text-center">Enter to send · Shift+Enter for new line</p>
      </div>
    </div>
  )
}

// ─── Main Export ──────────────────────────────────────────────────────────────

export default function InboxModule() {
  const [convos, setConvos] = useState<Conversation[]>(CONVERSATIONS)
  const [activeId, setActiveId] = useState<string>(CONVERSATIONS[0].id)
  const [filter, setFilter] = useState<Filter>('All')
  const [notice, setNotice] = useState<string | null>(null)

  const activeConv = convos.find(c => c.id === activeId) ?? convos[0]
  const totalUnread = convos.reduce((s, c) => s + c.unread, 0)

  const displayed = convos.filter(c => {
    if (filter === 'Unread') return c.unread > 0
    if (filter === 'All') return true
    return c.type === filter
  })

  const handleSend = (convId: string, body: string) => {
    setConvos(prev => prev.map(c => {
      if (c.id !== convId) return c
      const newMsg: Message = { id: `m${Date.now()}`, from: 'me', body, time: 'Just now', read: true }
      return { ...c, messages: [...c.messages, newMsg], lastMsg: body, time: 'Just now', unread: 0 }
    }))
  }

  const handleSelect = (id: string) => {
    setActiveId(id)
    setConvos(prev => prev.map(c => c.id === id ? { ...c, unread: 0 } : c))
  }

  const handleCompose = () => {
    const preferred = convos.find((c) => c.unread > 0) ?? convos[0]
    if (!preferred) {
      return
    }
    setFilter('All')
    handleSelect(preferred.id)
    setNotice(`Composer focused on ${preferred.name}.`)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">Inbox</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Unified messaging with tenants, vendors, and applicants
            {totalUnread > 0 && <span className="ml-2 font-bold text-violet-600">{totalUnread} unread</span>}
          </p>
        </div>
        <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={handleCompose}>
          <Send className="mr-1.5 h-3.5 w-3.5" />Compose
        </Button>
      </div>

      {notice && (
        <Card className="p-3 shadow-sm border-emerald-200 bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/25">
          <p className="text-xs text-emerald-800 dark:text-emerald-300">{notice}</p>
        </Card>
      )}

      <div className="flex gap-1.5 overflow-x-auto pb-0.5">
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={cn(
              'whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors capitalize',
              filter === f
                ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 border-slate-900 dark:border-slate-100'
                : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
            )}>
            {f === 'Unread' && totalUnread > 0 ? `Unread (${totalUnread})` : f}
          </button>
        ))}
      </div>

      <Card className="overflow-hidden shadow-sm" style={{ height: 'calc(100vh - 260px)', minHeight: '480px' }}>
        <div className="grid grid-cols-1 h-full xl:grid-cols-[320px_1fr]">
          <div className="border-r border-slate-200 dark:border-slate-700 overflow-y-auto">
            {displayed.length === 0 && (
              <div className="flex items-center justify-center h-32 text-sm text-slate-400">No conversations</div>
            )}
            {displayed.map(c => (
              <ConvItem key={c.id} conv={c} active={c.id === activeId} onClick={() => handleSelect(c.id)} />
            ))}
          </div>
          <div className="hidden xl:flex flex-col h-full">
            <AnimatePresence mode="wait">
              <motion.div key={activeId} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col h-full">
                <MessageThread conv={activeConv} onSend={handleSend} />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </Card>

      <div className="xl:hidden">
        {activeConv && (
          <Card className="shadow-sm overflow-hidden" style={{ height: '420px' }}>
            <div className="flex flex-col h-full">
              <MessageThread conv={activeConv} onSend={handleSend} />
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}
